import React, { useEffect, useState } from 'react';

import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useAppSelector } from '@core/redux/hooks';
import { useCreateProposal, useGetProposalLovs } from '@common/slices';
import { ErrorsOf, IAbstractRecord, TouchedOf } from '@models';
import { lookupListAsRecordObject } from '@utils/graph-utils';
import { isValidNumber } from '@utils/validationUtils';

import {
  proposalDrawerSections,
  proposalDrawerInitialValues
} from '@common/constants';

import { cloneDeep } from 'lodash';
import PrecisionService from '@core/services/PrecisionService';
import { IBaseDrawerProps } from '@common/types';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';

export function extractProposalLovs(data: any) {
  const partners = lookupListAsRecordObject(
    data?.production?.getProposalActiveBusinessPartners || [],
    true,
    'id',
    'fullName',
    'code'
  );

  const lines = lookupListAsRecordObject(
    data?.production?.getProposalLines || [],
    true,
    'id',
    'name',
    'code'
  );

  const sublines = lookupListAsRecordObject(
    data?.production?.getProposalSublines || [],
    true,
    'id',
    'name',
    'code'
  );

  const plans = lookupListAsRecordObject(
    data?.production?.getProposalPlans || [],
    true,
    'id',
    'name',
    'code'
  );

  const currencies = lookupListAsRecordObject(data?.core?.getCurrencies || []);

  const planCurrencies: Record<string, string> = {};
  data?.production?.getProposalPlans?.forEach((plan: any) => {
    if (plan?.id && plan?.planCurrency) {
      planCurrencies[plan.id] = plan.planCurrency;
    }
  });

  return { partners, lines, sublines, plans, currencies, planCurrencies };
}

export const transformToShare = (value: string | number) => {
  return isValidNumber(value) ? PrecisionService.divideBy100(Number(value)) : 0;
};

// Proposal drawer types and interfaces
interface Props extends IBaseDrawerProps {
  renewalContext?: {
    isRenewal: boolean;
    policyId?: string;
    policyNumber?: string;
    policyExpiryDate?: string;
    lineId?: string;
    lineCode?: string;
    lineName?: string;
    sublineId?: string;
    sublineName?: string;
    planId?: string;
    planName?: string;
    businessPartners?: Array<{
      businessPartnerId: string;
      businessPartnerCode: string;
      businessPartnerName: string;
      sharePercentage: number;
      isPrimary: boolean;
    }>;
    policyCurrencyID?: string;
  };
}

export interface IProposalDrawerValues {
  businessPartnerId: string;
  line: string;
  subline: string;
  plan: string;
  policyCurrency: string;
  isGroup: boolean;
  isRenewal: boolean;
  renewedFromPolicy: string;
  policyEffectiveDate: string;
  renewWithExistingPlan: boolean;
}

export interface IProposalLovs {
  line: IAbstractRecord;
  subline: IAbstractRecord;
  plan: IAbstractRecord;
  partners: IAbstractRecord;
  planCurrency: IAbstractRecord;
}

const NewProposalDrawer: React.FC<Props> = ({ renewalContext, onSuccess }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const user = useAppSelector((state) => state.user);
  const userAccess = user.info.access as 'Backoffice' | 'POS';

  const [lists, setLists] = useState<IProposalLovs>({
    line: {},
    subline: {},
    plan: {},
    partners: {},
    planCurrency: {}
  });
  const [showGroupPolicy, setShowGroupPolicy] = useState(false);
  const [planCurrencies, setPlanCurrencies] = useState<Record<string, string>>(
    {}
  );
  const [values, setValues] = useState<IProposalDrawerValues>();
  const [inputsForm, setInputsForm] = useState(() =>
    proposalDrawerSections(
      proposalDrawerInitialValues,
      lists,
      showGroupPolicy,
      renewalContext,
      userAccess
    )
  );

  const [getProposalLovsQuery] = useGetProposalLovs.useLazy();
  const [createProposalAction] = useCreateProposal();

  const initialize = async () => {
    const [proposalLovsResult] = await Promise.all([
      getProposalLovsQuery({
        variables: { lineIDs: [], sublineIDs: [] },
        errorPolicy: 'all'
      })
    ]);

    const { partners, lines, sublines, plans, currencies, planCurrencies } =
      extractProposalLovs(proposalLovsResult.data);

    const newLists: IProposalLovs = {
      line: lines,
      subline: sublines,
      plan: plans,
      partners,
      planCurrency: currencies
    };

    const renewalBusinessPartnerId =
      renewalContext?.businessPartners?.find((bp) => bp.isPrimary)
        ?.businessPartnerId ||
      renewalContext?.businessPartners?.[0]?.businessPartnerId ||
      '';

    const initialValues: IProposalDrawerValues = {
      ...proposalDrawerInitialValues,
      businessPartnerId:
        userAccess === 'POS'
          ? Object.keys(partners)[0] || ''
          : renewalBusinessPartnerId ||
            proposalDrawerInitialValues.businessPartnerId,
      line: renewalContext?.lineId || proposalDrawerInitialValues.line,
      subline: renewalContext?.sublineId || proposalDrawerInitialValues.subline,
      plan: renewalContext?.planId || proposalDrawerInitialValues.plan,
      policyCurrency:
        renewalContext?.policyCurrencyID ||
        proposalDrawerInitialValues.policyCurrency,
      isRenewal: Boolean(renewalContext?.isRenewal),
      renewedFromPolicy:
        renewalContext?.policyId ||
        proposalDrawerInitialValues.renewedFromPolicy,
      policyEffectiveDate:
        renewalContext?.policyExpiryDate ||
        proposalDrawerInitialValues.policyEffectiveDate,
      renewWithExistingPlan:
        renewalContext?.isRenewal ??
        proposalDrawerInitialValues.renewWithExistingPlan
    };

    setValues(initialValues);
    setLists(newLists);
    setPlanCurrencies(planCurrencies);
    setInputsForm(
      proposalDrawerSections(
        initialValues,
        newLists,
        showGroupPolicy,
        renewalContext,
        userAccess
      )
    );
  };

  useEffect(() => {
    initialize();
  }, []);

  const onCustomChange = async (
    fieldName: keyof IProposalDrawerValues,
    newValue: any,
    sectionIndex: number,
    allValues: IProposalDrawerValues,
    errors: ErrorsOf<IProposalDrawerValues>,
    touched: TouchedOf<IProposalDrawerValues>,
    additionalProperties?: {
      subFieldName?: string;
      index?: number;
    }
  ) => {
    const newLists = cloneDeep(lists);
    (allValues[fieldName] as any) = newValue;

    // plan‐dependent logic remains unchanged:
    if (fieldName === 'line') {
      const { data } = await getProposalLovsQuery({
        variables: { lineIDs: [newValue], sublineIDs: [] },
        errorPolicy: 'all'
      });

      const { sublines } = extractProposalLovs(data);
      newLists.subline = sublines;

      const lineCode = newLists.line[newValue].split(' -')[0]?.trim();

      const shouldShowGroupPolicy = ['3', '4', '51'].includes(lineCode);
      const isGroup = lineCode === '6';

      setShowGroupPolicy(shouldShowGroupPolicy);

      allValues.isGroup = isGroup;
      allValues.subline = '';
      allValues.plan = '';
      allValues.policyCurrency = '';
      newLists.plan = {};

      setValues(allValues);
      setLists(newLists);
      setInputsForm(
        proposalDrawerSections(
          allValues,
          newLists,
          shouldShowGroupPolicy,
          renewalContext,
          userAccess
        )
      );

      return;
    }

    if (fieldName === 'subline') {
      const { data } = await getProposalLovsQuery({
        variables: {
          lineIDs: [allValues.line],
          sublineIDs: [newValue]
        },
        errorPolicy: 'all'
      });
      const { plans, planCurrencies: newPlanCurrencies } =
        extractProposalLovs(data);
      newLists.plan = plans;
      setPlanCurrencies(newPlanCurrencies);
      allValues.plan = '';
    }

    if (fieldName === 'plan') {
      const currencyId = planCurrencies[newValue];
      if (currencyId) {
        allValues.policyCurrency = currencyId;
      }
    }

    setValues(allValues);
    setLists(newLists);
    setInputsForm(
      proposalDrawerSections(
        allValues,
        newLists,
        showGroupPolicy,
        renewalContext,
        userAccess
      )
    );
  };

  const onSubmit = async (data: IProposalDrawerValues) => {
    const businessPartnerInput = {
      businessPartnerID: data.businessPartnerId,
      sharePercentage: 1,
      policyBusinessPartnerType: 'PRIMARY'
    };

    const isRenewal = Boolean(renewalContext?.isRenewal || data.isRenewal);
    const renewedFrom =
      renewalContext?.policyId || data.renewedFromPolicy || undefined;

    const input = {
      lineID: data.line,
      sublineID: data.subline,
      planID: data.plan,
      currencyID: data.policyCurrency,
      groupPolicy: data.isGroup,
      isRenewal,
      renewedFrom: isRenewal ? renewedFrom : undefined,
      businessPartnerInput: [businessPartnerInput],
      renewWithExistingPlan: data.renewWithExistingPlan,
      policyEffectiveDate: isRenewal
        ? data.policyEffectiveDate || renewalContext?.policyExpiryDate || null
        : null
    };

    const result = await createProposalAction({ input: input });
    const proposalId = result.data.production.createProposal;
    onSuccess(`/production/proposal/${proposalId}`);
  };

  return (
    <DynamicFormWidget
      onChange={onCustomChange}
      sections={inputsForm}
      onSubmit={() => onSubmit(values!)}
      submitButtonState={submitButtonState}
    />
  );
};

export { NewProposalDrawer };
